import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import TimerIcon from "@mui/icons-material/Timer";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { ContactRound, MessageCircleMore, TabletSmartphone, Newspaper, Images, Music, MapPin, Timer, Calendar } from 'lucide-react';


const features = [
    {
        icon: <TabletSmartphone sx={{ fontSize: 22 }} />,
        title: "Beautiful Designs",
        description: "Dozens of elegant templates crafted for every occasion.",
    },
    {
        icon: <Newspaper sx={{ fontSize: 22 }} />,
        title: "RSVP",
        description: "Collect confirmations and heartfelt messages with ease.",
    },
    {
        icon: <Images sx={{ fontSize: 22 }} />,
        title: "Photo Gallery",
        description: "A stunning gallery for your most precious moments.",
    },
    {
        icon: <Music sx={{ fontSize: 22 }} />,
        title: "Background Music",
        description: "Set the perfect mood as guests open your invitation.",
    },
    {
        icon: <MapPin sx={{ fontSize: 22 }} />,
        title: "Navigation Map",
        description: "Embed directions so guests find your venue easily.",
    },
    {
        icon: <Timer sx={{ fontSize: 22 }} />,
        title: "Live Countdown",
        description: "A real-time timer counting down to your big day.",
    },
    {
        icon: <CardGiftcardIcon sx={{ fontSize: 22 }} />,
        title: "Digital Gift",
        description: "Accept digital gifts or wishlists — modern and seamless.",
    },
    {
        icon: <QrCode2Icon sx={{ fontSize: 22 }} />,
        title: "DuitNow QR",
        description: "Share your QR Bank for instant gift donations.",
    },
    {
        icon: <ContactRound sx={{ fontSize: 22 }} />,
        title: "Contact Information",
        description: "Display all event contact details clearly so guests can reach you easily.",
    },
    {
        icon: <MessageCircleMore sx={{ fontSize: 22 }} />,
        title: "Wish Section",
        description: "Let guests leave warm messages and congratulations straight on your card.",
    },
    {
        icon: <AutoAwesomeIcon sx={{ fontSize: 22 }} />,
        title: "Particle Effect",
        description: "Add enchanting animated particles to bring your invitation to life.",
    },
    {
        icon: <Calendar sx={{ fontSize: 22 }} />,
        title: "Calendar",
        description: "Let guests save your event date directly to their personal calendar.",
    },
];

const FeatureCard = ({ icon, title, description }) => {
    return (
        <Box
            sx={{
                // Glass card

                backdropFilter: "blur(18px)",
                WebkitBackdropFilter: "blur(18px)",
                borderRadius: "20px",
                p: "24px 22px",
                height: "100%",
                boxSizing: "border-box",
                backgroundColor: "#ffffff",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                transition: "transform 0.28s ease, box-shadow 0.28s ease, background 0.28s ease",
                cursor: "default",
                "&:hover": {
                    transform: "translateY(-5px)",
                    background: "rgba(255, 255, 255, 0.72)",
                    boxShadow: "0 12px 36px rgba(85, 194, 195, 0.16), 0 2px 8px rgba(0,0,0,0.06)",
                },
                "&:hover .feature-icon-box": {
                    background: "rgba(85, 194, 195, 0.18)",
                },
            }}
        >
            {/* Icon pill */}
            <Box
                className="feature-icon-box"
                sx={{
                    width: "42px",
                    height: "42px",
                    borderRadius: "12px",
                    background: "rgba(85, 194, 195, 0.10)",
                    border: "1px solid rgba(85, 194, 195, 0.20)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#3DA8A9",
                    flexShrink: 0,
                    transition: "background 0.28s ease",
                }}
            >
                {icon}
            </Box>

            <Box>
                <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: "#1A2C2C", letterSpacing: "0.01em", mb: 0.5, fontSize: "0.9rem" }}
                >
                    {title}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{ color: "rgba(30, 60, 60, 0.58)", lineHeight: 1.65, display: "block", fontSize: "0.78rem" }}
                >
                    {description}
                </Typography>
            </Box>
        </Box>
    );
};

const HomeFeatures = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

    return (
        <Box
            sx={{
                position: "relative",
                py: isMobile ? 5 : 5,
                px: isMobile ? 2 : 0,
                overflow: "hidden",
                // // Soft gradient background that blends with the site theme
                // background:
                //     "linear-gradient(160deg, #E6FAF9 0%, #f0fcfc 40%, #eaf9f9 70%, #E6FAF9 100%)",
            }}
        >



            <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
                {/* ── Section Header ── */}
                <Box sx={{ textAlign: "center", mb: isMobile ? 3 : 7 }}>


                    <Typography
                        variant={isMobile ? "h6" : "h6"}
                        fontWeight={600}
                        sx={{
                            color: "#0F2828",
                            letterSpacing: "-0.5px",
                            lineHeight: 1.2,
                            mb: 2,
                        }}
                    >
                        Our Features
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            color: "rgba(15, 40, 40, 0.50)",
                            maxWidth: "400px",
                            mx: "auto",
                            lineHeight: 1.9,
                            fontSize: "0.88rem",
                        }}
                    >
                        Enjoy a range of modern features that enhance your invitation and make your special day truly unforgettable.
                    </Typography>
                </Box>

                {/* ── Feature Cards ── */}
                <Grid container spacing={isMobile ? 2.5 : 4}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <FeatureCard
                                icon={feature.icon}
                                title={feature.title}
                                description={feature.description}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default HomeFeatures;
